import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Shield, LogOut, RefreshCw } from 'lucide-react';

export default function PendingValidationPage() {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [validationCount, setValidationCount] = useState(0);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    if (user?.is_validated || user?.is_admin) {
      navigate('/feed', { replace: true });
    }
    setValidationCount(user?.validation_count || 0);
  }, [user, navigate]);

  // Periodically check if user has been validated
  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        await refreshUser();
      } catch (e) {
        // silently fail
      }
    }, 30000); // Check every 30 seconds
    return () => clearInterval(interval);
  }, [refreshUser]);

  const handleCheckStatus = async () => {
    setChecking(true);
    try {
      await refreshUser();
    } catch (e) {
      // silently fail
    } finally {
      setChecking(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-dark flex items-center justify-center p-6">
      <div className="max-w-md w-full">
        <div className="glass-card p-8 text-center border border-white/10">
          {/* Shield icon */}
          <div className="flex justify-center mb-6">
            <Shield className="w-16 h-16 text-gold" strokeWidth={1.5} />
          </div>

          {/* Title */}
          <h1 className="font-serif text-2xl text-gold mb-4">
            En attente de validation
          </h1>

          {/* Description */}
          <p className="text-gray-400 mb-6 leading-relaxed">
            Votre profil doit être validé par un administrateur ou par 5 ambassadeurs avant de pouvoir accéder au fil d&apos;actualité.
          </p>

          {/* Validation counter */}
          <div className="bg-dark-subtle border border-white/10 rounded-lg p-4 mb-6">
            <p className="text-white font-semibold mb-2">
              <span className="text-gold">{validationCount}/5</span> validations obtenues
            </p>
            <div className="w-full bg-dark rounded-full h-2">
              <div 
                className="bg-gold h-2 rounded-full transition-all duration-500"
                style={{ width: `${Math.min((validationCount / 5) * 100, 100)}%` }}
              />
            </div>
          </div>

          {/* Info */}
          <p className="text-gray-500 text-sm mb-8">
            Assurez-vous que votre profil est complet et que vos photos respectent nos recommandations.
          </p>

          {/* Actions */}
          <div className="space-y-3">
            <button
              onClick={handleCheckStatus}
              disabled={checking}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 border border-gold/30 text-gold hover:bg-gold/10 transition-colors rounded-sm text-sm"
              data-testid="check-validation-status"
            >
              <RefreshCw className={`w-4 h-4 ${checking ? 'animate-spin' : ''}`} />
              {checking ? 'Vérification...' : 'Vérifier mon statut'}
            </button>
            <button
              onClick={handleLogout}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 text-gray-500 hover:text-red-400 transition-colors text-sm"
              data-testid="pending-logout"
            >
              <LogOut className="w-4 h-4" />
              Se déconnecter
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
